import { useEffect, useState, type ReactNode } from 'react';
import type { FaqItem, Finding, PageSignals, Platform, ScanResult, Severity } from '../types';
import { draftFaq } from '../api';
import { getIndexEvidence, type IndexEvidence } from '../account';
import { findingHelpFor } from '../explanations';
import LiveTestOverlay from './LiveTestOverlay';
import WinsPanel, { type Win } from './WinsPanel';

// ── small UI atoms ───────────────────────────────────────────────────────────

type Level = 'High' | 'Medium' | 'Low' | 'Ongoing';
const LEVEL_STYLE: Record<Level, string> = {
  High: 'text-cyan border-cyan/40',
  Medium: 'text-estimated border-estimated/40',
  Low: 'text-text-secondary border-warm-dark-2',
  Ongoing: 'text-estimated border-estimated/40',
};
const SEV_IMPACT: Record<Severity, Level> = { critical: 'High', warning: 'Medium', info: 'Low' };

function Tag({ level, children }: { level: Level; children: ReactNode }): JSX.Element {
  return <span className={`font-mono text-[10px] uppercase tracking-wide border rounded px-1.5 py-0.5 ${LEVEL_STYLE[level]}`}>{children}</span>;
}

type Auto = 'done' | 'partial' | 'todo';
// Prominent, filled status chip so a "Done" step is unmistakable at a glance.
function DetectPill({ status }: { status: Auto }): JSX.Element {
  const map: Record<Auto, { s: string; t: string }> = {
    done: { s: 'bg-success text-warm-dark', t: '✓ Done, detected' },
    partial: { s: 'bg-estimated text-warm-dark', t: '◑ Partly present' },
    todo: { s: 'bg-[#E0574A] text-warm-dark', t: '✗ Not found, to do' },
  };
  return <span className={`font-mono text-[11px] uppercase tracking-wide font-semibold rounded px-2 py-0.5 ${map[status].s}`}>{map[status].t}</span>;
}
function DonePill(): JSX.Element {
  return <span className="font-mono text-[11px] uppercase tracking-wide font-semibold rounded px-2 py-0.5 bg-success text-warm-dark">✓ Done</span>;
}

function CopyButton({ text, label = 'copy' }: { text: string; label?: string }): JSX.Element {
  const [done, setDone] = useState(false);
  return (
    <button
      onClick={() => void navigator.clipboard.writeText(text).then(() => { setDone(true); setTimeout(() => setDone(false), 1500); })}
      className="font-mono text-xs border border-cyan/40 text-cyan rounded px-2 py-1 hover:bg-cyan/10 transition"
    >
      {done ? 'copied ✓' : label}
    </button>
  );
}

// ── derivations from the scanned page ────────────────────────────────────────

function brandNameFromUrl(u: string): string {
  try {
    const h = new URL(u).hostname.replace(/^www\./, '');
    const base = h.split('.')[0] ?? h;
    return base ? base.charAt(0).toUpperCase() + base.slice(1) : 'Your Brand';
  } catch { return 'Your Brand'; }
}
function originOf(u: string): string {
  try { return new URL(u).origin; } catch { return u; }
}
const norm = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

// Profiles we can recognise in a sameAs / outbound-link set.
const PLATFORMS: { key: string; label: string; rx: RegExp }[] = [
  { key: 'linkedin', label: 'LinkedIn', rx: /linkedin\.com/i },
  { key: 'youtube', label: 'YouTube', rx: /youtube\.com|youtu\.be/i },
  { key: 'wikidata', label: 'Wikidata', rx: /wikidata\.org/i },
  { key: 'wikipedia', label: 'Wikipedia', rx: /wikipedia\.org/i },
  { key: 'crunchbase', label: 'Crunchbase', rx: /crunchbase\.com/i },
  { key: 'github', label: 'GitHub', rx: /github\.com/i },
  { key: 'x', label: 'X (Twitter)', rx: /twitter\.com|(^|\/\/)([^/]*\.)?x\.com/i },
  { key: 'facebook', label: 'Facebook', rx: /facebook\.com/i },
  { key: 'instagram', label: 'Instagram', rx: /instagram\.com/i },
  // Review aggregators — a cheap third-party corroboration signal that AI reads.
  { key: 'g2', label: 'G2', rx: /g2\.com/i },
  { key: 'capterra', label: 'Capterra', rx: /capterra\./i },
  { key: 'trustpilot', label: 'Trustpilot', rx: /trustpilot\./i },
];
function detectProfiles(sameAs: string[]): Map<string, string> {
  const found = new Map<string, string>();
  for (const u of sameAs) for (const p of PLATFORMS) if (p.rx.test(u) && !found.has(p.key)) found.set(p.key, u);
  return found;
}

interface Detection {
  hasSignals: boolean;
  jsonLdTypes: string[];
  sameAs: string[];
  hasOrg: boolean;
  existingFaq: string[];
  hasFaqPage: boolean;
  profiles: Map<string, string>;
  /** Everything below is measured on the live page. `undefined` means the run
   * predates the measurement — then we say "can't tell", never "not done". */
  sig?: PageSignals;
}
function detect(result: ScanResult): Detection {
  const sig = result.signals;
  const jsonLdTypes = sig?.jsonLdTypes ?? [];
  const sameAs = sig?.sameAs ?? [];
  return {
    hasSignals: !!sig,
    jsonLdTypes,
    sameAs,
    hasOrg: sig?.hasOrganization ?? false,
    existingFaq: sig?.faqQuestions ?? [],
    hasFaqPage: jsonLdTypes.includes('FAQPage'),
    profiles: detectProfiles(sameAs),
    sig,
  };
}

/**
 * A method's detector returns `true` (in place), `false` (missing) or
 * `undefined` (we did not measure it — an older stored run, or something we
 * honestly cannot see from one page fetch). Undefined must never be rendered as
 * a red "not found": claiming a site lacks something we never checked is exactly
 * the kind of inflated finding this product refuses to make.
 */
type Applied = boolean | undefined;

// ── copy-paste JSON-LD + plan markdown ───────────────────────────────────────

function orgJsonLd(url: string, sameAs: string[]): string {
  const origin = originOf(url);
  const obj = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: brandNameFromUrl(url),
    url: origin,
    logo: `${origin}/logo.png`,
    sameAs: sameAs.length ? sameAs : ['https://www.linkedin.com/company/YOUR-COMPANY', 'https://www.youtube.com/@YOUR-CHANNEL', 'https://www.wikidata.org/wiki/YOUR-ITEM'],
  };
  return `<script type="application/ld+json">\n${JSON.stringify(obj, null, 2)}\n</script>`;
}
function faqJsonLd(items: FaqItem[]): string {
  const obj = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((it) => ({ '@type': 'Question', name: it.question, acceptedAnswer: { '@type': 'Answer', text: it.answer } })),
  };
  return `<script type="application/ld+json">\n${JSON.stringify(obj, null, 2)}\n</script>`;
}
function buildPlanMarkdown(url: string, gate: Finding[], onPage: Finding[], faq: FaqItem[], questions: string[], step1Done: boolean): string {
  const l: string[] = [`# AIO Fix it plan for ${url}`, ''];
  l.push('## 1. Let AI read you (impact: high · effort: low)');
  if (step1Done) l.push('- [x] AI crawlers can reach and read this page.');
  else gate.filter((f) => f.severity === 'critical').forEach((f) => l.push(`- [ ] ${f.title}: ${f.fix}`));
  l.push('- [ ] Submit the site to Bing Webmaster Tools as well as Google Search Console, because ChatGPT search runs on the Bing index.');
  l.push('', '## 2. Sharpen the page for citation (impact: medium · effort: low)');
  if (onPage.length) onPage.forEach((f) => l.push(`- [ ] ${f.title}, ${f.fix}`));
  else l.push('- [x] Structure and entity signals look good.');
  l.push('', '## 3. Answer the real buyer questions (impact: high · effort: medium)');
  if (faq.length) faq.forEach((it) => l.push(`### ${it.question}`, it.answer, ''));
  else if (questions.length) questions.forEach((q) => l.push(`- ${q}`));
  else l.push('- Generate the buyer questions from your Visibility / Footprint scan.');
  l.push('', '## 4. Build off-site presence (impact: high · effort: ongoing)');
  for (const m of OFF_SITE_WAYS) l.push(`- [ ] ${m.title}: ${m.desc}`);
  l.push('');
  l.push('_Off-site signals take weeks to be crawled, indexed and absorbed. Readiness moves first; citations follow._');
  l.push('_Re-scan Visibility whenever you make changes to see “absent” turn into “mentioned”._');
  return l.join('\n');
}

// ── Step / FixCard ───────────────────────────────────────────────────────────

function Step({
  n, title, subtitle, impact, effort, done, autoStatus, checked, onToggle, children,
}: {
  n: number; title: string; subtitle: string; impact: Level; effort: Level;
  done: boolean; autoStatus?: Auto; checked: boolean; onToggle: () => void; children: ReactNode;
}): JSX.Element {
  return (
    <div className="flex gap-4">
      <div className="flex flex-col items-center">
        <div className={`w-9 h-9 shrink-0 rounded-full font-mono font-semibold flex items-center justify-center ${done ? 'bg-success text-warm-dark' : 'bg-cyan text-warm-dark'}`}>
          {done ? '✓' : n}
        </div>
        <div className="flex-1 w-px bg-warm-dark-2 mt-2" />
      </div>
      <div className={`flex-1 pb-8 ${done ? 'pl-3 border-l-2 border-success/50 -ml-3' : ''}`}>
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className={`font-serif text-xl ${done ? 'text-success' : ''}`}>{title}</h3>
          {done ? <DonePill /> : autoStatus ? <DetectPill status={autoStatus} /> : null}
        </div>
        <div className="flex items-center gap-2 flex-wrap mt-0.5">
          <Tag level={impact}>impact {impact}</Tag>
          <Tag level={effort}>effort {effort}</Tag>
        </div>
        <p className="text-sm text-text-secondary mb-2 mt-1">{subtitle}</p>
        <label className="inline-flex items-center gap-2 mb-3 text-sm text-text-secondary cursor-pointer select-none">
          <input type="checkbox" checked={checked} onChange={onToggle} className="accent-cyan" />
          Mark handled
        </label>
        {children}
      </div>
    </div>
  );
}

function FixCard({ f }: { f: Finding }): JSX.Element {
  const help = findingHelpFor(f.title);
  return (
    <div className="border border-warm-dark-2 rounded-lg p-3 mb-2">
      <p className="text-sm text-text-primary flex items-center gap-2">
        {f.title}
        <Tag level={SEV_IMPACT[f.severity]}>impact {SEV_IMPACT[f.severity]}</Tag>
      </p>
      <p className="text-sm text-cyan/80 mt-1">→ {f.fix}</p>
      {help?.tools && help.tools.length > 0 && (
        <p className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-sm">
          <span className="text-text-secondary">How / tools:</span>
          {help.tools.map((t) => (
            <a key={t.url} href={t.url} target="_blank" rel="noreferrer" className="text-cyan hover:underline">
              {t.label} ↗
            </a>
          ))}
        </p>
      )}
    </div>
  );
}

const PLATFORM_LABEL: Record<Platform, string> = {
  wordpress: 'WordPress', shopify: 'Shopify', wix: 'Wix', webflow: 'Webflow', squarespace: 'Squarespace', unknown: 'your site',
};

// ── "More ways" — each with a More expander + a concrete example for this site,
//    and an optional detector so what's already applied shows as Done (#6). ─────

interface Method {
  title: string;
  free: boolean;
  desc: string;
  more: string;
  example: (brand: string, origin: string) => string;
  link?: { label: string; url: string };
  detect?: (d: Detection) => Applied;
}

/**
 * On-page methods — rendered under step 2.
 *
 * Everything below the first four entries came out of running this on a real
 * site end-to-end (a WordPress B2B site, months of measurement): they are the
 * things that had to be built by hand after the obvious schema work was done,
 * and that no generic AEO checklist mentioned.
 */
const ON_PAGE_WAYS: Method[] = [
  {
    title: 'Organization + sameAs schema',
    free: true,
    desc: 'The canonical way to tell AI which profiles are you (LinkedIn, YouTube, Crunchbase…).',
    more: 'Add one Organization JSON-LD block in your homepage <head> with name, url, logo and a sameAs array listing every official profile. This is what lets AI collapse all your profiles into one entity.',
    example: (b, o) => `For ${b}: an Organization node with "url":"${o}", your logo URL, and sameAs listing your LinkedIn + YouTube + any Wikidata/Crunchbase pages.`,
    link: { label: 'Organization schema', url: 'https://schema.org/Organization' },
    detect: (d) => d.hasOrg && d.sameAs.length > 0,
  },
  {
    title: 'Author / Person schema + a visible last-updated date',
    free: true,
    desc: 'Mark up who wrote it, and say when it was last updated. Expertise plus freshness.',
    more: 'On articles and guides add a Person node as the author with their own sameAs (LinkedIn), and put a visible "last updated" date on the page as well as dateModified in the schema. AI weights recent, attributed content more heavily, and a stale-looking page gets skipped for a competitor\'s newer one.',
    example: (b) => `On each ${b} article: author { "@type":"Person", name, sameAs:["<their LinkedIn>"] } plus a visible "Last updated: <month year>" line.`,
    detect: (d) => (d.sig ? d.jsonLdTypes.includes('Person') && Boolean(d.sig.modifiedDate) : undefined),
  },
  {
    title: 'WebSite, BreadcrumbList & FAQPage schema',
    free: true,
    desc: 'These structured-data types make your pages machine-parseable so AI can pull clean, attributable chunks.',
    more: 'WebSite gives your site an identity + search action; BreadcrumbList exposes hierarchy; FAQPage makes Q&A retrievable. Most SEO plugins (e.g. Yoast) emit WebSite + BreadcrumbList automatically.',
    example: (b) => `${b} should have WebSite + BreadcrumbList (often auto from your SEO plugin) and a FAQPage on your help/FAQ page.`,
    link: { label: 'Rich Results Test', url: 'https://search.google.com/test/rich-results' },
    detect: (d) => ['WebSite', 'BreadcrumbList', 'FAQPage'].every((t) => d.jsonLdTypes.includes(t)),
  },
  {
    title: 'A plain, entity-clear About page',
    free: true,
    desc: 'One page that states, in one sentence, who you are and the specific problem you own.',
    more: 'This is what AI quotes when asked "who is X". Lead with a single clear sentence, then the specifics.',
    example: (b, o) => `${o}/about should open with one sentence: "${b} is a … that helps … do …".`,
  },
  {
    title: 'A direct answer in the first 2–3 sentences under the H1',
    free: true,
    desc: 'The single highest-leverage rewrite. AI quotes the first clear statement after the heading, so put the answer there.',
    more: 'Most pages open with problem-agitation ("Do you struggle with…? Then you know how hard…") and only answer several paragraphs later. AI extracts the top of the page, finds no answer, and moves on. Put a plain 2–3 sentence answer to the page\'s own headline question directly under the H1, then keep the persuasion below it. Nothing else is being lost. The sales copy stays, it just moves down.',
    example: (b) => `If a ${b} page is headed "How do I do X with Y?", the next paragraph must literally answer it: "You do X with Y by … It takes … It works with …".`,
  },
  {
    title: 'Answer-first headings + descriptive internal links',
    free: true,
    desc: 'Question-shaped headings, the answer immediately under each, and links whose anchor names the topic.',
    more: 'AI extracts the first clear sentence under a heading. Put the answer there, then elaborate. Internal links should say the topic, not "click here". The anchor text is how AI learns what the target page is about.',
    example: (b) => `On ${b} pages, turn headings into questions and put a one-line answer directly beneath each.`,
  },
  {
    title: 'Concrete numbers, dates and named facts',
    free: true,
    desc: 'Pages with specific figures get quoted; pages of adjectives do not.',
    more: 'A claim AI can attribute needs something checkable in it: how many customers, how long an implementation takes, what the latency is, what it costs, what year you started. Vague superlatives ("fast", "reliable", "leading") give AI nothing to cite. Only publish figures you can stand behind. A wrong number is worse than none.',
    example: (b) => `Replace "${b} is fast and reliable" with "${b} syncs in under X seconds and is live in Y working days for Z+ customers".`,
    // Roughly one checkable figure per hundred words is where pages stop reading
    // as adjectives. Below that there is nothing for AI to attribute.
    detect: (d) => (d.sig?.numberDensityPer1k === undefined ? undefined : d.sig.numberDensityPer1k >= 10),
  },
  {
    title: 'A comparison table and numbered steps',
    free: true,
    desc: 'Tables and ordered lists are the formats AI lifts most readily. A wall of prose is not.',
    more: 'Add one <table> that lays out the concrete facts (what works with what, which direction data flows, what each tier includes) and one numbered "how it works" list. Both are self-contained chunks AI can quote whole and attribute to you.',
    example: (b) => `On the main ${b} service page: one table of "what / which option / which direction" plus a numbered 4–6 step "how it works".`,
    detect: (d) => (d.sig?.hasTables === undefined ? undefined : d.sig.hasTables && Boolean(d.sig.hasLists)),
  },
  {
    title: 'Service (or Product) schema linked to your Organization by @id',
    free: true,
    desc: 'Turns each thing you sell into its own machine-readable, citable unit rather than prose on a page.',
    more: 'Add a Service (or Product) JSON-LD node per service page, with provider pointing at your Organization node via @id. Organization alone tells AI who you are; Service tells it what you specifically do, per page, in a form it can quote.',
    example: (b, o) => `On each ${b} service page: {"@type":"Service","name":"…","provider":{"@id":"${o}/#organization"}}.`,
    link: { label: 'Service schema', url: 'https://schema.org/Service' },
    detect: (d) => d.jsonLdTypes.includes('Service') || d.jsonLdTypes.includes('Product'),
  },
  {
    title: 'One canonical page per topic, and kill the duplicates',
    free: true,
    desc: 'Two pages on the same topic split the signal and neither gets cited. This one actively hurts.',
    more: 'Sites accumulate a thin older page and a rich newer page on the same subject, often with the identical H1. AI (and search) cannot tell which is authoritative, so the topic gets weaker, not stronger. Pick the richer page as canonical and either 301-redirect the thin one onto it or point rel=canonical at it. One URL per question.',
    example: (b, o) => `Search ${o} for two pages sharing an H1. Keep the richer one, redirect the other. Do it before writing anything new for ${b}.`,
    link: { label: 'Google on canonicalization', url: 'https://developers.google.com/search/docs/crawling-indexing/consolidate-duplicate-urls' },
    // We can see whether this page declares a canonical; we cannot see from one
    // fetch whether a duplicate exists elsewhere. Detecting the tag is the honest
    // half, and the copy says the rest.
    detect: (d) => d.sig?.hasCanonical,
  },
  {
    title: 'One crawlable page per thing you actually do',
    free: true,
    desc: 'The pages that get cited are narrow and specific: one per service, location, or supported combination.',
    more: 'A single broad "what we do" page loses to competitors who publish one page per exact thing a buyer searches for. Give every distinct service, tool you work with, or area you serve its own page with its own H1, direct answer and FAQ. This is usually the fastest win available, because it needs no one else\'s approval.',
    example: (b) => `Instead of one ${b} services page, publish one page per concrete offering, each answering the exact question a buyer would type.`,
  },
  {
    title: 'hreflang, if you publish in more than one language',
    free: true,
    desc: 'Without it, your language versions look like duplicates of each other rather than one entity in two languages.',
    more: 'Emit hreflang tags (one per language plus x-default) in the <head>. Multilingual plugins often ship this switched off or emit it too late in the head. Check the rendered HTML, not just the setting.',
    example: (b, o) => `If ${b} publishes at ${o}/ and ${o}/en/, both need reciprocal hreflang tags plus an x-default.`,
    link: { label: 'Google on hreflang', url: 'https://developers.google.com/search/docs/specialty/international/localized-versions' },
    detect: (d) => (d.sig?.hreflangCount === undefined ? undefined : d.sig.hreflangCount > 0),
  },
  {
    title: 'Consistent NAP across site + profiles',
    free: true,
    desc: 'Same name, logo, handle and details everywhere.',
    more: 'Consistency lets entity resolution collapse all your profiles into one brand. Audit that name/logo/handles match across site, LinkedIn, YouTube and any listings. Include the real registered address and phone in your Organization schema. A concrete address is a strong disambiguation signal.',
    example: (b) => `Make sure "${b}" (exact spelling), the same logo and the same handles appear identically everywhere.`,
  },
];

/**
 * Off-site methods — rendered under step 4, ordered by impact-to-effort.
 * This is where the decider actually lives: measured on a real site, on-page
 * readiness went 79 → 97 with the citation count still at zero, because nothing
 * off the site had changed yet.
 */
const OFF_SITE_WAYS: Method[] = [
  {
    title: 'Get listed in the app store / marketplace of the platforms you work with',
    free: true,
    desc: 'The strongest third-party proof there is: the platform itself confirming you do this. Usually why a competitor is named and you are not.',
    more: 'When AI is asked "how do I connect A to B", it reaches for pages on A\'s and B\'s own domains. A listing in a partner\'s app store, marketplace or partner directory is that page. Registration is normally free; the work is packaging what you already do into something listable and passing their review, which takes weeks, so start it early and do the rest in parallel.',
    example: (b) => `List ${b} in the app store or partner directory of every major platform it integrates with, with the platform\'s name in the listing title.`,
  },
  {
    title: 'Review platforms: G2, Capterra, Trustpilot',
    free: true,
    desc: 'Cheap, fast third-party corroboration. Even a handful of real reviews is enough to get quoted.',
    more: 'Create and claim the free vendor profile on the platforms that matter in your category, fill in categories, description and website, then ask genuinely happy customers for a review. Small counts still get cited. The threshold is much lower than people assume. Follow each platform\'s rules on incentives, and never post fake reviews: it is both against their terms and against the point.',
    example: (b, o) => `Claim ${b} on G2, Capterra and Trustpilot with ${o}, then ask a handful of happy customers. Add the profile URLs to your sameAs.`,
    link: { label: 'G2, list your product', url: 'https://www.g2.com/products/new' },
    detect: (d) => d.profiles.has('g2') || d.profiles.has('capterra') || d.profiles.has('trustpilot'),
  },
  {
    title: 'Create a Wikidata item, and link it both ways',
    free: true,
    desc: 'Wikidata feeds the knowledge graphs behind many models. The link has to point both ways to count.',
    more: 'Create an item for your brand with the official website property (P856), headquarters, industry, and external IDs (LinkedIn, Crunchbase, YouTube). Then add the Wikidata URL back into your Organization sameAs. One-directional links are weak; it is the round trip that makes AI treat the item and the site as one verified entity.',
    example: (b, o) => `New Wikidata item "${b}", official website = ${o}, add your LinkedIn/Crunchbase/YouTube IDs, then add that Wikidata URL to your sameAs.`,
    link: { label: 'New Wikidata item', url: 'https://www.wikidata.org/wiki/Special:NewItem' },
    detect: (d) => d.profiles.has('wikidata'),
  },
  {
    title: 'Crunchbase profile, verified and linked both ways',
    free: true,
    desc: 'A second independent record confirming the company exists, with the same name and website.',
    more: 'Register, then verify and correct it, auto-created profiles routinely carry the wrong legal name, the wrong parent company or the wrong categories, which actively confuses entity resolution. Then link it from your sameAs so it and the site point at each other.',
    example: (b, o) => `Claim ${b} on Crunchbase, check the name/parent/categories are right, set the website to ${o}, and add the profile to your sameAs.`,
    link: { label: 'Crunchbase', url: 'https://www.crunchbase.com/' },
    detect: (d) => d.profiles.has('crunchbase'),
  },
  {
    title: 'Google Business Profile + Bing Places',
    free: true,
    desc: 'A verified listing puts your entity into the knowledge graphs AI models draw on. Essential if you serve a place.',
    more: 'Claim and verify the free listings. Use the exact same name, website and details as on your site so entity resolution ties them together. (This is the local-business listing, separate from Bing Webmaster Tools in step 1, which is about being indexed at all.)',
    example: (b, o) => `Register "${b}" with website ${o} and your real address/category, then mirror the same details on Bing Places.`,
    link: { label: 'Google Business Profile', url: 'https://www.google.com/business/' },
  },
  {
    title: 'Partner and industry directories',
    free: true,
    desc: 'Independent domains naming you next to the problem. Volume matters more than any single one.',
    more: 'Get into the directories of the tools, suppliers and associations you already work with, and make sure partners and customers who reference you actually link to you. Each independent domain that ties your name to the problem is another corroboration of the entity.',
    example: (b) => `List ${b} in every partner/integration directory you qualify for, and ask existing partners to link back from their pages.`,
  },
  {
    title: 'Your own honest comparison and "best X" articles',
    free: true,
    desc: 'The comparison pages AI quotes are largely written by vendors. Yours can be one of them.',
    more: 'Write the genuine buyer\'s guide for your category: the question, a direct answer, the selection criteria, a table of the real options, including yours, assessed honestly, and including cases where a competitor fits better. That honesty is what makes it citable; a puff piece reads as marketing and gets ignored.',
    example: (b) => `Publish "Best <category> in <market> <year>" and "<approach A> vs <approach B>", listing ${b} among the options on the same criteria as everyone else.`,
  },
  {
    title: 'Joint PR and partner co-mentions',
    free: false,
    desc: 'One authoritative page that frames you and a known partner together does more than ten generic mentions.',
    more: 'A trade-press piece about a partnership names both companies and states what each one does, exactly the association AI reuses when answering. Repeat it with each significant partner. Note that self-submitted "wire" posts carry less weight than earned editorial.',
    example: (b) => `Run one joint announcement per significant ${b} partner, per quarter, in the trade press your buyers actually read.`,
  },
  {
    title: 'Wikipedia, only if you genuinely qualify',
    free: true,
    desc: 'Top-tier citation source, but it needs independent coverage you do not control.',
    more: 'Wikipedia requires significant coverage in independent, reliable sources. Without that, an article will be deleted and the attempt can rebound on you. Treat it as a later consequence of the PR work above, not a starting move.',
    example: (b) => `Revisit ${b} on Wikipedia once independent press coverage exists, not before.`,
    detect: (d) => d.profiles.has('wikipedia'),
  },
];

function MethodCard({ m, brand, origin, applied }: { m: Method; brand: string; origin: string; applied: Applied }): JSX.Element {
  const checkable = m.detect !== undefined;
  return (
    <div className={`border rounded-lg p-3 ${applied === true ? 'border-success/40 bg-success/5' : 'border-warm-dark-2'}`}>
      <p className="text-sm text-text-primary flex items-center gap-2 flex-wrap">
        {m.title}
        {applied === true ? (
          <span className="font-mono text-[10px] uppercase tracking-wide font-semibold rounded px-1.5 py-0.5 bg-success text-warm-dark">✓ found on your page</span>
        ) : applied === false ? (
          <span className="font-mono text-[10px] uppercase tracking-wide border rounded px-1.5 py-0.5 text-[#E0574A] border-[#E0574A]/40">✗ not found</span>
        ) : (
          <>
            {/* Not measured — say so rather than implying it's missing. */}
            {checkable && <span className="font-mono text-[10px] uppercase tracking-wide border rounded px-1.5 py-0.5 text-text-secondary border-warm-dark-2" title="This run predates the check, re-scan to have it measured.">not checked</span>}
            {!checkable && <span className="font-mono text-[10px] uppercase tracking-wide border rounded px-1.5 py-0.5 text-text-secondary border-warm-dark-2" title="This cannot be verified from your page. It lives off your site or needs a human eye.">not checkable from here</span>}
          </>
        )}
        {applied !== true && m.free && <span className="font-mono text-sm text-measured border border-measured/40 rounded px-1">free</span>}
      </p>
      <p className="text-sm text-text-secondary mt-1">{m.desc}</p>
      <details className="mt-1">
        <summary className="font-mono text-xs text-cyan cursor-pointer">More ↓</summary>
        <p className="text-sm text-text-secondary mt-1">{m.more}</p>
        <p className="text-sm text-text-primary/90 mt-1"><span className="text-text-secondary">Example for your site: </span>{m.example(brand, origin)}</p>
        {m.link && (
          <a href={m.link.url} target="_blank" rel="noreferrer" className="text-sm text-cyan hover:underline">{m.link.label} ↗</a>
        )}
      </details>
    </div>
  );
}

/**
 * Which assistant reads what, and whether this site lets it. Two different gates
 * get confused constantly: letting the crawler in (robots.txt — measurable here)
 * and being in the index it searches (submission — not visible from a page, so
 * it is stated as an action, never scored).
 */
const ASSISTANTS: { name: string; reads: string; bots: string[] }[] = [
  { name: 'ChatGPT (search)', reads: 'the Bing index, plus its own crawler', bots: ['OAI-SearchBot', 'GPTBot'] },
  { name: 'Google AI Overviews · Gemini', reads: "Google's index", bots: ['Google-Extended'] },
  { name: 'Perplexity', reads: 'its own live crawl', bots: ['PerplexityBot'] },
  { name: 'Claude', reads: 'its own crawler', bots: ['ClaudeBot', 'anthropic-ai'] },
  { name: 'Open models generally', reads: 'Common Crawl', bots: ['CCBot'] },
];

const BOT_STYLE: Record<string, string> = {
  allowed: 'text-success border-success/40',
  blocked: 'text-[#E0574A] border-[#E0574A]/40',
  unspecified: 'text-text-secondary border-warm-dark-2',
};

function IndexingBlock({ d, origin }: { d: Detection; origin: string }): JSX.Element {
  const bots = d.sig?.aiBots;
  const verdict = (b: string): string => bots?.[b] ?? 'unspecified';

  /*
    The half this block used to leave as homework.

    It said "check your key pages report as indexed" and never checked. Every
    probe already stores the citations behind the answer, and a citation to your
    own URL is the search engine showing it had that page indexed, so the answer
    was in the database the whole time.

    Only for a signed-in account, because it is built from that account's runs.
    A one-off anonymous scan has nothing to build it from and keeps the plain
    instruction.
  */
  const [ev, setEv] = useState<IndexEvidence | null>(null);
  useEffect(() => {
    let host = '';
    try { host = new URL(origin).hostname.replace(/^www\./, ''); } catch { return; }
    if (!host) return;
    void getIndexEvidence(host).then(setEv).catch(() => setEv(null));
  }, [origin]);
  const proven = ev?.pages ?? [];
  return (
    <div className="border border-cyan/30 rounded-lg p-3 mt-2 bg-cyan/5">
      <p className="text-sm text-text-primary">Be in the indexes AI actually reads</p>
      <p className="text-sm text-text-secondary mt-1">
        Every assistant reads a different source. Two separate things have to be true for each one:
        its crawler must be allowed in (below, measured from your robots.txt), and your pages must be
        in the index it searches (submission. You have to do that yourself).
      </p>

      {bots ? (
        <div className="overflow-x-auto mt-2">
          <table className="w-full text-sm">
            <thead><tr className="text-text-secondary font-mono text-xs text-left">
              <th className="py-1 pr-3">Assistant</th><th className="pr-3">Reads</th><th>Its crawler on your site</th>
            </tr></thead>
            <tbody>
              {ASSISTANTS.map((a) => (
                <tr key={a.name} className="border-t border-warm-dark-2 align-top">
                  <td className="py-1.5 pr-3 text-text-primary">{a.name}</td>
                  <td className="pr-3 text-text-secondary">{a.reads}</td>
                  <td className="py-1.5">
                    <span className="flex flex-wrap gap-1">
                      {a.bots.map((b) => (
                        <span key={b} className={`font-mono text-[11px] border rounded px-1.5 py-0.5 ${BOT_STYLE[verdict(b)]}`}>
                          {b} {verdict(b) === 'allowed' ? '✓' : verdict(b) === 'blocked' ? '✗ blocked' : '· not mentioned'}
                        </span>
                      ))}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="font-mono text-xs text-text-secondary/70 mt-1">
            “not mentioned” in robots.txt means allowed by default. That is fine. “blocked” is the one to fix.
          </p>
        </div>
      ) : (
        <p className="font-mono text-xs text-text-secondary/70 mt-2">Re-scan to have the individual crawlers checked.</p>
      )}

      {/* The submission half — actions, not scores. */}
      <div className="mt-3 pt-3 border-t border-warm-dark-2">
        <p className="text-sm text-text-primary">Submit the site, Bing as well as Google</p>
        <p className="text-sm text-text-secondary mt-1">
          ChatGPT&apos;s search is built on the <span className="text-text-primary">Bing</span> index. Almost everyone
          registers with Google Search Console and stops, which leaves them invisible to ChatGPT no matter how good the
          page is. Register {origin} in both, submit your sitemap in both, and check your key pages report as indexed.
          IndexNow (built into Bing) pushes new pages immediately instead of waiting to be crawled.
        </p>
        <div className="flex flex-wrap gap-3 mt-2">
          <a href="https://www.bing.com/webmasters" target="_blank" rel="noreferrer" className="text-sm text-cyan hover:underline">Bing Webmaster Tools ↗</a>
          <a href="https://search.google.com/search-console" target="_blank" rel="noreferrer" className="text-sm text-cyan hover:underline">Google Search Console ↗</a>
          <a href="https://www.indexnow.org/" target="_blank" rel="noreferrer" className="text-sm text-cyan hover:underline">IndexNow ↗</a>
        </div>

        <div className="flex flex-wrap gap-1.5 mt-3">
          <span className={`font-mono text-[11px] border rounded px-2 py-0.5 ${d.sig?.sitemapFound === undefined ? 'text-text-secondary border-warm-dark-2' : d.sig.sitemapFound ? 'text-success border-success/40' : 'text-[#E0574A] border-[#E0574A]/40'}`}>
            {d.sig?.sitemapFound === undefined ? '· sitemap not checked' : d.sig.sitemapFound ? '✓ sitemap found' : '✗ no sitemap, submit one'}
          </span>
          <span className={`font-mono text-[11px] border rounded px-2 py-0.5 ${d.sig?.llmsTxtFound === undefined ? 'text-text-secondary border-warm-dark-2' : d.sig.llmsTxtFound ? 'text-success border-success/40' : 'text-text-secondary border-warm-dark-2'}`}>
            {d.sig?.llmsTxtFound === undefined ? '· llms.txt not checked' : d.sig.llmsTxtFound ? '✓ llms.txt present' : '· no llms.txt (minor)'}
          </span>
          <span className={`font-mono text-[11px] border rounded px-2 py-0.5 ${d.sig?.renderDependencyRisk === undefined ? 'text-text-secondary border-warm-dark-2' : d.sig.renderDependencyRisk === 'low' ? 'text-success border-success/40' : 'text-[#E0574A] border-[#E0574A]/40'}`}>
            {d.sig?.renderDependencyRisk === undefined ? '· render not checked'
              : d.sig.renderDependencyRisk === 'low' ? '✓ content in the HTML'
              : `✗ ${d.sig.renderDependencyRisk} JS-render risk, crawlers may see an empty page`}
          </span>
        </div>
        {/* Measured, where there is anything to measure. */}
        {ev && (ev.pages.length > 0 || ev.domainSeenIn.length > 0 || ev.runs > 0) && (
          <div className="mt-3 pt-3 border-t border-warm-dark-2">
            <p className="text-sm text-text-primary">What has actually turned up in an index</p>
            {ev.summary.map((line, i) => (
              <p key={i} className="text-sm text-text-secondary mt-1 max-w-prose">{line}</p>
            ))}

            {proven.length > 0 && (
              <details className="mt-2">
                <summary className="font-mono text-xs text-cyan cursor-pointer">
                  the {proven.length} {proven.length === 1 ? 'page' : 'pages'} an index has shown ↓
                </summary>
                <div className="overflow-x-auto mt-2">
                  <table className="w-full text-sm">
                    <thead><tr className="text-text-secondary font-mono text-xs text-left">
                      <th className="py-1 pr-3">Page</th><th className="pr-3">Index</th>
                      <th className="pr-3">Cited</th><th>Last seen</th>
                    </tr></thead>
                    <tbody>
                      {proven.slice(0, 25).map((p) => (
                        <tr key={`${p.index}${p.url}`} className="border-t border-warm-dark-2 align-top">
                          <td className="py-1.5 pr-3 text-text-primary break-all">
                            {p.url.replace(/^https?:\/\//, '')}
                            {p.exampleQuery && (
                              <span className="block text-text-secondary/70 text-xs">
                                found on &ldquo;{p.exampleQuery}&rdquo;
                              </span>
                            )}
                          </td>
                          <td className="pr-3 font-mono text-xs text-measured">{p.index}</td>
                          <td className="pr-3 font-mono text-xs text-text-secondary">{p.timesCited}×</td>
                          <td className="font-mono text-xs text-text-secondary">
                            {new Date(p.lastSeenAt).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}

            <p className="font-mono text-xs text-text-secondary/70 mt-2 max-w-prose">
              This is proof, never absence. A page listed here was cited by that engine, so it was in the
              index at that moment. A page not listed has simply not been the best answer to a question
              yet, and that is not the same as not being indexed.
            </p>
          </div>
        )}

        <p className="font-mono text-xs text-text-secondary/70 mt-2">
          Submission itself is not visible from your page, so do it, then tick step 1.
        </p>
      </div>
    </div>
  );
}

/**
 * Where a mention is worth having. AI names the brands the wider web talks
 * about, but not every page carries the same weight — these are the site types
 * that repeatedly turn up as the sources behind an answer. Ordered by how much
 * they move the needle for the effort.
 *
 * `key` matches a PLATFORMS entry where we can actually detect it in sameAs;
 * the rest are stated as work to do, never as a red mark, because a page on
 * someone else's domain is not visible from yours.
 */
const THIRD_PARTY: { group: string; note: string; sites: { name: string; url: string; key?: string; note: string }[] }[] = [
  {
    group: 'Knowledge bases, how AI decides you exist',
    note: 'Structured, machine-readable records. These feed the knowledge graphs models are grounded on. Link each one back from your Organization sameAs so the pair is verifiable in both directions.',
    sites: [
      { name: 'Wikidata', url: 'https://www.wikidata.org/wiki/Special:NewItem', key: 'wikidata', note: 'open, anyone can create one, no notability bar for a real company' },
      { name: 'Crunchbase', url: 'https://www.crunchbase.com/', key: 'crunchbase', note: 'verify and correct it, auto-created profiles often carry the wrong legal name or parent' },
      { name: 'Wikipedia', url: 'https://en.wikipedia.org/wiki/Wikipedia:Notability_(organizations_and_companies)', key: 'wikipedia', note: 'only with independent press coverage; a deleted article is worse than none' },
      { name: 'LinkedIn company page', url: 'https://www.linkedin.com/company/setup/new/', key: 'linkedin', note: 'the baseline record almost every model has seen' },
    ],
  },
  {
    group: 'Review platforms, third-party corroboration, cheap and fast',
    note: 'Even a handful of genuine reviews is enough to get quoted; the threshold is far lower than people assume. Follow each platform\'s rules on incentives and never post fake reviews.',
    sites: [
      { name: 'G2', url: 'https://www.g2.com/products/new', key: 'g2', note: 'software / B2B' },
      { name: 'Capterra', url: 'https://www.capterra.com/vendors/sign-up', key: 'capterra', note: 'software / B2B, feeds GetApp + Software Advice too' },
      { name: 'Trustpilot', url: 'https://business.trustpilot.com/signup', key: 'trustpilot', note: 'any sector, strong in Europe' },
      { name: 'Google Business Profile', url: 'https://www.google.com/business/', note: 'essential if you serve a place; feeds the Knowledge Graph' },
    ],
  },
  {
    group: 'Marketplaces & partner directories. The strongest signal there is',
    note: 'A page on a partner\'s own domain saying you do this thing. When AI answers "how do I do X with <platform>", it reaches for pages on that platform\'s domain. Free to apply for, slow to get through review, start early.',
    sites: [
      { name: 'App stores of the platforms you integrate with', url: '', note: 'the single highest-impact listing for an integration business' },
      { name: 'Partner / reseller directories of your suppliers', url: '', note: 'usually just an email to your account manager' },
      { name: 'Industry & trade association listings', url: '', note: 'independent domain, sector-relevant, often free with membership' },
      { name: 'Local chamber of commerce / business register entries', url: '', note: 'weak individually, useful as corroboration of the legal entity' },
    ],
  },
  {
    group: 'Where people actually ask your question',
    note: 'Models lean heavily on discussion and comparison pages when a question has no obvious authority. Take part honestly as yourself, astroturfing gets detected, removed, and remembered.',
    sites: [
      { name: 'Reddit', url: 'https://www.reddit.com/', note: 'heavily cited by AI answers; answer real threads in your niche, disclose who you are' },
      { name: 'Stack Overflow / GitHub', url: 'https://github.com/', key: 'github', note: 'if you ship code or an API, public docs and repos get read directly' },
      { name: 'YouTube', url: 'https://www.youtube.com/', key: 'youtube', note: 'transcripts are indexed; mentions here correlate strongly with AI visibility' },
      { name: 'Quora / sector Q&A sites', url: 'https://www.quora.com/', note: 'lower weight, but cheap and it compounds' },
    ],
  },
  {
    group: 'Earned coverage',
    note: 'One authoritative page framing you next to a known partner does more than ten generic mentions. Self-submitted "wire" posts carry noticeably less weight than earned editorial.',
    sites: [
      { name: 'Trade press in your sector', url: '', note: 'the publications your buyers already read' },
      { name: 'Joint announcements with partners', url: '', note: 'names both companies and states what each does, exactly the association AI reuses' },
      { name: 'Podcasts and interviews', url: '', note: 'transcribed and indexed, but insist on a backlink, they are often forgotten' },
      { name: 'Customer case studies on the customer\'s own site', url: '', note: 'independent domain, names you, describes the problem you solved' },
    ],
  },
];

function ThirdPartyBlock({ d, brand }: { d: Detection; brand: string }): JSX.Element {
  return (
    <details className="mt-3 border border-warm-dark-2 rounded-lg p-3">
      <summary className="font-mono text-sm text-cyan cursor-pointer">
        Third-party sites worth being mentioned on ↓
      </summary>
      <p className="text-sm text-text-secondary mt-2">
        AI names the brands the rest of the web talks about. These are the site types that keep turning up as the
        sources behind an answer. Where a link is visible in your schema it is marked; everything else lives on
        someone else&apos;s domain, so it genuinely cannot be checked from here.
      </p>
      <div className="mt-3 space-y-3">
        {THIRD_PARTY.map((g) => (
          <div key={g.group}>
            <p className="text-sm text-text-primary">{g.group}</p>
            <p className="text-sm text-text-secondary mb-1.5">{g.note}</p>
            <ul className="space-y-1">
              {g.sites.map((site) => {
                const linked = site.key ? d.profiles.has(site.key) : undefined;
                return (
                  <li key={site.name} className="text-sm">
                    <span className="flex items-baseline gap-2 flex-wrap">
                      {linked === true && <span className="font-mono text-[10px] uppercase tracking-wide font-semibold rounded px-1.5 py-0.5 bg-success text-warm-dark shrink-0">✓ linked</span>}
                      {site.url
                        ? <a href={site.url} target="_blank" rel="noreferrer" className="text-cyan hover:underline">{site.name} ↗</a>
                        : <span className="text-text-primary">{site.name}</span>}
                      <span className="text-text-secondary">· {site.note}</span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
      <p className="text-sm text-text-secondary mt-3 pt-2 border-t border-warm-dark-2">
        Whichever you get, add the URL to {brand}&apos;s <span className="font-mono">Organization sameAs</span> and make
        sure the profile links back to your site. A one-way link is a claim; a round trip is what lets AI treat the
        profile and the site as one verified entity.
      </p>
    </details>
  );
}

// ── main ─────────────────────────────────────────────────────────────────────

export default function FixIt({
  result,
  questions,
  faq,
  setFaq,
  absentQuestions = [],
  wins = [],
}: {
  result: ScanResult;
  questions: string[];
  faq: FaqItem[];
  setFaq: (items: FaqItem[]) => void;
  absentQuestions?: string[];
  /** Questions where AI already names or cites you — the only evidence in the
   * whole report of what actually worked, so it leads the plan. */
  wins?: Win[];
}): JSX.Element {
  const [faqLoading, setFaqLoading] = useState(false);
  const [faqError, setFaqError] = useState<string | null>(null);

  const findings = result.readiness.findings;
  const gate = findings.filter((f) => f.category === 'crawlability');
  const criticalGate = gate.filter((f) => f.severity === 'critical');
  const minorGate = gate.filter((f) => f.severity !== 'critical');
  const onPage = findings.filter((f) => f.category === 'structure' || f.category === 'entity' || f.category === 'citability');
  const platform: Platform = result.platform ?? 'unknown';

  const d = detect(result);
  const brand = brandNameFromUrl(result.url);
  const origin = originOf(result.url);

  // Suggested questions not yet covered by an existing on-page FAQ (#6d).
  const existingSet = new Set(d.existingFaq.map(norm));
  const missingSuggested = questions.filter((q) => !existingSet.has(norm(q)));

  // Auto-detected status per step (undefined when the run predates page signals).
  const step1Done = criticalGate.length === 0;
  const step2Status: Auto | undefined = !d.hasSignals ? undefined
    : (d.hasOrg && d.sameAs.length > 0 && onPage.length === 0) ? 'done'
      : (d.hasOrg || d.sameAs.length > 0) ? 'partial' : 'todo';
  const step3Status: Auto | undefined = !d.hasSignals ? undefined
    : (d.hasFaqPage && missingSuggested.length === 0) ? 'done'
      : d.hasFaqPage ? 'partial' : 'todo';
  const step4Status: Auto | undefined = !d.hasSignals ? undefined
    : (d.profiles.has('linkedin') && d.profiles.has('youtube')) ? 'done'
      : d.profiles.size > 0 ? 'partial' : 'todo';

  // Checklist (#6f) — manual "handled" override, persisted per URL.
  const CHECK_KEY = `aivs_fixit_${result.url}`;
  const [checked, setChecked] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem(CHECK_KEY) ?? '[]') as string[]); } catch { return new Set(); }
  });
  function toggleStep(key: string): void {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      try { localStorage.setItem(CHECK_KEY, JSON.stringify([...next])); } catch { /* ignore */ }
      return next;
    });
  }
  const stepDone: Record<number, boolean> = {
    1: step1Done || checked.has('step-1'),
    2: step2Status === 'done' || checked.has('step-2'),
    3: step3Status === 'done' || checked.has('step-3'),
    4: step4Status === 'done' || checked.has('step-4'),
  };
  const doneCount = [1, 2, 3, 4].filter((n) => stepDone[n]).length;

  async function onGenerateFaq(): Promise<void> {
    if (faqLoading) return;
    setFaqLoading(true);
    setFaqError(null);
    try {
      const r = await draftFaq(result.url, questions);
      const companyFacts: FaqItem[] = [
        { question: 'Where is the company based?', answer: 'TODO: add your registered seat here (headquarters city and country).' },
        { question: 'When was the company founded?', answer: 'TODO: add the year you were founded.' },
      ];
      setFaq([...r.items, ...companyFacts]);
    } catch (err) {
      setFaqError(err instanceof Error ? err.message : 'FAQ generation failed');
    } finally {
      setFaqLoading(false);
    }
  }

  const faqMarkdown = faq.map((it) => `### ${it.question}\n${it.answer}`).join('\n\n');
  const planMarkdown = buildPlanMarkdown(result.url, gate, onPage, faq, questions, step1Done);
  const detectedProfileLabels = PLATFORMS.filter((p) => d.profiles.has(p.key)).map((p) => p.label);
  const missingProfiles = PLATFORMS.filter((p) => !d.profiles.has(p.key));

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Big overlay while FAQ answers are drafted, same as the live tests (#1). */}
      {faqLoading && <LiveTestOverlay phase="faq" />}
      <div className="mb-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <h2 className="font-serif text-2xl mb-1">Fix it: your action plan</h2>
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs text-text-secondary">{doneCount}/4 done</span>
            <CopyButton text={planMarkdown} label="copy whole plan (md)" />
          </div>
        </div>
        <p className="text-sm text-text-secondary">
          Do these in order. Your live page was checked and whatever is <span className="text-success">already applied</span> is marked done. The
          rest is what is genuinely missing. This tells you exactly what to add; it does not change your site for you.
        </p>
      </div>

      <WinsPanel wins={wins} brand={brand} domain={new URL(origin).hostname.replace(/^www\./, '')} />

      <Step n={1} title="Let AI read you" subtitle="The gate. If AI can't fetch and read the page, nothing else matters."
        impact="High" effort="Low" done={stepDone[1]} autoStatus={step1Done ? 'done' : 'todo'}
        checked={checked.has('step-1')} onToggle={() => toggleStep('step-1')}>
        {step1Done ? (
          <p className="text-sm text-success">✓ AI crawlers can reach and read this page. The status is OK and robots.txt doesn't block the citing bots. Nothing to do here.</p>
        ) : (
          criticalGate.map((f, i) => <FixCard key={i} f={f} />)
        )}
        {minorGate.length > 0 && (
          <details className="mt-2">
            <summary className="font-mono text-xs text-text-secondary cursor-pointer">Minor crawlability notes ({minorGate.length}) ↓</summary>
            <div className="mt-2">{minorGate.map((f, i) => <FixCard key={i} f={f} />)}</div>
          </details>
        )}

        <IndexingBlock d={d} origin={origin} />
      </Step>

      <Step n={2} title="Sharpen the page for citation" subtitle="Make you an unambiguous entity with clean, quotable content."
        impact="Medium" effort="Low" done={stepDone[2]} autoStatus={step2Status}
        checked={checked.has('step-2')} onToggle={() => toggleStep('step-2')}>

        {/* Organization schema — Done when detected, otherwise ready-to-paste JSON-LD. */}
        {d.hasSignals && d.hasOrg ? (
          <div className="border border-measured/40 rounded-lg p-3 mb-2 bg-measured/5">
            <p className="text-sm text-text-primary">✓ Organization schema detected, AI can identify your entity.</p>
            <p className="text-sm text-text-secondary mt-1">
              {detectedProfileLabels.length
                ? <>Linked profiles found in your schema: <span className="text-text-primary">{detectedProfileLabels.join(', ')}</span>. Check the logo and every profile URL are present and correct.</>
                : 'No sameAs profiles found inside it though, add your LinkedIn / YouTube / Wikidata URLs to the sameAs array (see step 4).'}
            </p>
          </div>
        ) : d.hasSignals ? (
          <div className="border border-cyan/30 rounded-lg p-3 mb-2 bg-cyan/5">
            <div className="flex items-center justify-between gap-2 mb-1">
              <p className="text-sm text-text-primary">✗ No Organization schema found, paste this in your <span className="font-mono">&lt;head&gt;</span></p>
              <CopyButton text={orgJsonLd(result.url, d.sameAs)} label="copy JSON-LD" />
            </div>
            <p className="text-sm text-text-secondary mb-2">Pre-filled with your name and domain{d.sameAs.length ? ' and the profiles already found on your page' : ''}. Replace any <span className="font-mono">YOUR-…</span> placeholders.</p>
            <pre className="text-xs bg-warm-dark rounded p-2 overflow-x-auto font-mono text-text-secondary">{orgJsonLd(result.url, d.sameAs)}</pre>
          </div>
        ) : null}

        {onPage.length === 0 ? (
          <p className="text-sm text-success">✓ Structure and entity signals look good.</p>
        ) : (
          onPage.map((f, i) => <FixCard key={i} f={f} />)
        )}

        <details className="mt-2">
          <summary className="font-mono text-sm text-cyan cursor-pointer">More ways to strengthen the page (mostly free) ↓</summary>
          <div className="mt-2 space-y-2">
            {ON_PAGE_WAYS.map((m) => (
              <MethodCard key={m.title} m={m} brand={brand} origin={origin} applied={d.hasSignals ? m.detect?.(d) : undefined} />
            ))}
          </div>
        </details>
      </Step>

      <Step n={3} title="Answer the real buyer questions" subtitle="Publish answers to the exact questions buyers ask AI: an FAQ it can retrieve and cite."
        impact="High" effort="Medium" done={stepDone[3]} autoStatus={step3Status}
        checked={checked.has('step-3')} onToggle={() => toggleStep('step-3')}>

        {/* Existing on-page FAQ vs the questions from your scan (#6d). */}
        {d.hasSignals && d.hasFaqPage && (
          <div className="border border-measured/40 rounded-lg p-3 mb-3 bg-measured/5">
            <p className="text-sm text-text-primary">✓ FAQPage JSON-LD detected. You already publish {d.existingFaq.length} FAQ{d.existingFaq.length === 1 ? '' : 's'} AI can parse.</p>
            {missingSuggested.length > 0 ? (
              <>
                <p className="text-sm text-text-secondary mt-1">Your scan surfaced questions your FAQ doesn't cover yet. Add answers to these and keep them in the FAQPage schema:</p>
                <ul className="text-sm text-text-secondary list-disc pl-5 mt-1 space-y-0.5">
                  {missingSuggested.map((q, i) => <li key={i}>{q}</li>)}
                </ul>
              </>
            ) : (
              <p className="text-sm text-text-secondary mt-1">It already covers the questions from your scan. Nicely done, keep it current.</p>
            )}
          </div>
        )}
        {d.hasSignals && !d.hasFaqPage && (
          <div className="border border-estimated/40 rounded-lg p-3 mb-3 bg-estimated/5">
            <p className="text-sm text-text-primary">✗ No FAQPage schema found on this page.</p>
            <p className="text-sm text-text-secondary mt-1">
              Publishing answers isn't enough. They <span className="text-text-primary">must be wrapped in FAQPage JSON-LD</span> or AI can't parse
              them as retrievable Q&amp;A. Draft answers below, then paste the generated FAQPage JSON-LD into the page.
            </p>
          </div>
        )}

        {/* Where AI didn't name you — highest-leverage content (#6d). */}
        {absentQuestions.length > 0 && (
          <div className="border border-estimated/40 rounded-lg p-3 mb-3 bg-estimated/5">
            <p className="text-sm text-text-primary mb-1">AI didn't name you for these, start here</p>
            <p className="text-sm text-text-secondary mb-2">
              In your scan, AI answered these without mentioning you. Clear, citable answers to exactly these questions are the
              highest-leverage content you can write.
            </p>
            <ul className="text-sm text-text-secondary list-disc pl-5 space-y-0.5">
              {absentQuestions.map((q, i) => <li key={i}>{q}</li>)}
            </ul>
          </div>
        )}

        <p className="text-sm text-text-secondary mb-2">
          These are the specific questions from your Visibility / Footprint scan. Publish clear answers on{' '}
          {PLATFORM_LABEL[platform]}: a dedicated FAQ / help page with{' '}
          <span className="font-mono">FAQPage</span> JSON-LD so AI can parse and quote them.
        </p>

        <div className="border border-cyan/30 rounded-lg p-3 mb-3 bg-cyan/5">
          <p className="text-sm text-text-primary mb-1">Add your company facts too</p>
          <p className="text-sm text-text-secondary">
            Include an "About" FAQ that states your registered seat (headquarters city and country) and the year you
            were founded. These stable entity facts make AI far more confident it is citing the right organisation, and
            they line up with your <span className="font-mono">Organization</span> schema (<span className="font-mono">foundingDate</span>,{' '}
            <span className="font-mono">address</span>).
          </p>
        </div>

        {platform === 'wordpress' && (
          <div className="border border-cyan/30 rounded-lg p-3 mb-3 bg-cyan/5">
            <p className="text-sm text-text-primary mb-1">You're on WordPress → use PageCoder RAG Chat</p>
            <p className="text-sm text-text-secondary">
              My RAG Chat plugin turns these questions into an on-site FAQ / answer bot grounded in your own content,
              which is exactly the retrievable, citable content AI needs.
            </p>
            <a href="https://pagecoder.ai/products/rag-chat/" target="_blank" rel="noreferrer" className="inline-block mt-2 font-mono text-sm text-cyan border border-cyan/40 rounded px-3 py-1 hover:bg-cyan/10 transition">
              PageCoder RAG Chat ↗
            </a>
          </div>
        )}

        {faq.length === 0 && (
          <div className="mb-2">
            <button
              onClick={onGenerateFaq}
              disabled={faqLoading}
              className="bg-cyan text-warm-dark font-medium rounded-lg px-5 py-2 text-sm disabled:opacity-50 hover:opacity-90 transition"
            >
              {faqLoading ? 'Drafting answers from your content…' : 'Draft FAQ answers from my content →'}
            </button>
            <p className="text-sm text-text-secondary mt-1">
              {questions.length > 0
                ? 'Uses the questions from your scan. Concrete answers are drafted from your own pages. Review, edit and publish them.'
                : 'The buyer questions are generated from your pages, with grounded answers drafted alongside them. Review, edit and publish them.'}
            </p>
          </div>
        )}
        {faqError && (
          <p className="font-mono text-sm text-[#E0574A] border border-[#E0574A]/40 rounded-lg px-3 py-2 mt-2">
            Couldn&apos;t draft the answers: {faqError}
          </p>
        )}

        {faq.length > 0 && (
          <div>
            <div className="flex justify-end gap-2 mb-2">
              <CopyButton text={faqMarkdown} label="copy all (markdown)" />
              <CopyButton text={faqJsonLd(faq)} label="copy FAQPage JSON-LD" />
            </div>
            {faq.map((it, i) => (
              <div key={i} className="border border-warm-dark-2 rounded-lg p-3 mb-2">
                <p className="text-sm text-text-primary font-medium">{it.question}</p>
                <p className="text-sm text-text-secondary mt-1">{it.answer}</p>
              </div>
            ))}
            <p className="text-sm text-text-secondary">
              Draft. Review for accuracy before publishing. Add these as an FAQ page and wrap them in the FAQPage JSON-LD above. The schema is what makes them citable.
            </p>
          </div>
        )}
      </Step>

      <Step n={4} title="Build off-site presence" subtitle="The real decider. AI names brands the wider web talks about. This lives off your website."
        impact="High" effort="Ongoing" done={stepDone[4]} autoStatus={step4Status}
        checked={checked.has('step-4')} onToggle={() => toggleStep('step-4')}>

        {/* Which profiles your schema already links — detected from sameAs (#6). */}
        {d.hasSignals && (
          <div className="border border-warm-dark-2 rounded-lg p-3 mb-2">
            <p className="text-sm text-text-primary mb-1">Profiles linked from your page</p>
            <div className="flex flex-wrap gap-1.5">
              {(['linkedin', 'youtube'] as const).map((k) => {
                const label = PLATFORMS.find((p) => p.key === k)!.label;
                const on = d.profiles.has(k);
                return (
                  <span key={k} className={`font-mono text-xs border rounded px-2 py-1 ${on ? 'text-success border-success/40' : 'text-[#E0574A] border-[#E0574A]/40'}`}>
                    {on ? '✓' : '✗'} {label}
                  </span>
                );
              })}
              {detectedProfileLabels.filter((l) => l !== 'LinkedIn' && l !== 'YouTube').map((l) => (
                <span key={l} className="font-mono text-xs border rounded px-2 py-1 text-success border-success/40">✓ {l}</span>
              ))}
            </div>
            <p className="text-sm text-text-secondary mt-2">
              {d.profiles.has('linkedin') && d.profiles.has('youtube')
                ? 'LinkedIn and YouTube are linked. Make sure the link is bidirectional (each profile links back to your site) so the pairing is verifiable.'
                : 'Add the missing ones to your Organization sameAs, and link each profile back to your site. YouTube mentions correlate strongest with AI visibility (r ≈ 0.74).'}
            </p>
          </div>
        )}

        {/* Other connections worth adding — the ones we did NOT find (#6). */}
        {d.hasSignals && missingProfiles.length > 0 && (
          <div className="border border-cyan/30 rounded-lg p-3 mb-2 bg-cyan/5">
            <p className="text-sm text-text-primary mb-1">Other connections to add</p>
            <ul className="text-sm text-text-secondary space-y-1">
              {missingProfiles.filter((p) => p.key !== 'linkedin' && p.key !== 'youtube').map((p) => (
                <li key={p.key}>
                  <span className="text-text-primary">{p.label}:</span>{' '}
                  {p.key === 'wikidata' ? `create a Wikidata item for ${brand} and link it (strongest knowledge-graph signal).`
                    : p.key === 'crunchbase' ? `list ${brand} on Crunchbase with ${origin}.`
                    : p.key === 'wikipedia' ? 'if you qualify, a Wikipedia article is a top-tier citation source.'
                    : p.key === 'github' ? `link your GitHub org if you ship code.`
                    : `add your ${p.label} profile and reference it in sameAs.`}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* The off-site playbook, ordered by impact-to-effort. What's already in
            your sameAs shows as applied, same as step 2. */}
        <p className="text-sm text-text-secondary mb-2">
          Ordered by impact for the effort. Start the slow ones (listings, reviews, PR) now and do the fast ones while
          they are in review, none of them depends on the others.
        </p>
        <div className="space-y-2">
          {OFF_SITE_WAYS.map((m) => (
            <MethodCard key={m.title} m={m} brand={brand} origin={origin} applied={d.hasSignals ? m.detect?.(d) : undefined} />
          ))}
        </div>

        <ThirdPartyBlock d={d} brand={brand} />

        {/* Honesty about the lag — measured on a real site: readiness 79 → 97
            with citations still at zero, because off-site takes weeks. */}
        <div className="border border-estimated/40 rounded-lg p-3 mt-3 bg-estimated/5">
          <p className="text-sm text-text-primary">Expect a lag. This is the honest part</p>
          <p className="text-sm text-text-secondary mt-1">
            On-page fixes show up in your readiness score immediately. Off-site work does not: new listings, profiles and
            mentions have to be crawled, indexed and then absorbed into what the models retrieve, which typically takes
            weeks. I have watched a site go from 79 to 97 readiness with its citation count still at zero, purely because
            the off-site signals were days old. That is expected, not failure. Readiness is the leading indicator; citations
            follow it.
          </p>
        </div>

        <p className="text-sm text-text-secondary mt-3">
          Re-scan Visibility whenever you've made changes to see “absent” turn into “mentioned”. That's the loop, run it
          as often as makes sense for you.
        </p>
      </Step>
    </div>
  );
}
